import AppPagination from "@/component/common/AppPagination.tsx";
import ConfirmButton from "@/component/common/button/ConfirmButton.tsx";
import ManageContentLayout from "@/component/common/layout/ManageContentLayout.tsx";
import { MANAGER_PATH } from "@/constant/url-path.ts";
import { ProductService } from "@/services/product/ProductService.ts";
import { toast } from "@/utils/toast.tsx";
import { PlusOutlined } from "@ant-design/icons";
import { Avatar, Button, Flex, Space, Table, TableProps } from "antd";
import Search from "antd/es/input/Search";
import { TFunction } from "i18next";
import * as jdenticon from "jdenticon";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const generateHeaderTable = (
  t: TFunction,
  research: () => void,
  searchRequest: SearchProductListRequest,
  navigation: ReturnType<typeof useNavigate>,
): TableProps<ProductDetail>["columns"] => {
  return [
    {
      title: t("field.no"),
      dataIndex: "no",
      key: "no",
      align: "center",
      render: (_, _record, index) => <span>{index + 1}</span>,
    },
    {
      title: t("field.productName"),
      dataIndex: "productName",
      key: "productName",
      sorter: true,
      sortOrder:
        (searchRequest?.sortBy?.[0] === "productName" &&
          (searchRequest?.sortDesc?.[0] ? "descend" : "ascend")) ||
        undefined,
      render: (_, record) => {
        const name = record?.productName || record?.name || "";
        const initials = name
          .split(" ")
          .filter(Boolean)
          .map((n) => n[0])
          .slice(0, 2)
          .join("")
          .toUpperCase();

        const seedRaw = String(
          record?.productCode ?? record?.productName ?? name ?? "unknown",
        );
        const thumbSize = 40;
        const svg = jdenticon.toSvg(seedRaw, thumbSize);
        const avatarDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

        return (
          <div className={"flex items-center"}>
            <Avatar
              size={thumbSize}
              src={avatarDataUrl}
              style={{ backgroundColor: "#87d068" }}
            >
              {initials || "?"}
            </Avatar>
            <Button
              type="link"
              onClick={() => {
                const params = new URLSearchParams();
                params.append("productCode", String(record?.productCode));
                navigation(`${MANAGER_PATH.PRODUCT.EDIT}?${params.toString()}`);
              }}
              style={{ marginLeft: 8 }}
            >
              {record?.productName ?? record?.name ?? "-"}
            </Button>
          </div>
        );
      },
    },

    {
      title: t("field.version"),
      dataIndex: "version",
      key: "version",
      align: "center",
      sorter: true,
      sortOrder:
        (searchRequest?.sortBy?.[0] === "version" &&
          (searchRequest?.sortDesc?.[0] ? "descend" : "ascend")) ||
        undefined,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => {
            const params = new URLSearchParams();
            params.append("productCode", String(record?.productCode));
            navigation(`${MANAGER_PATH.PRODUCT.EDIT}?${params.toString()}`);
          }}
        >
          {record?.version ?? "-"}
        </Button>
      ),
    },
    {
      title: t("field.action"),
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <EditButton record={record} />
          <DeleteButton record={record} research={research} />
        </Space>
      ),
    },
  ];
};

const EditButton = ({ record }: { record: ProductDetail }) => {
  const { t } = useTranslation();
  const navigation = useNavigate();

  return (
    <Button
      color={"primary"}
      variant={"filled"}
      onClick={() => {
        const params = new URLSearchParams();
        params.append("productCode", String(record?.productCode));
        navigation(`${MANAGER_PATH.PRODUCT.EDIT}?${params.toString()}`);
      }}
    >
      {t("button.edit")}
    </Button>
  );
};

const DeleteButton = ({
  record,
  research,
}: {
  record: ProductDetail;
  research: () => void;
}) => {
  const { t } = useTranslation();

  const handleDeleteUser = async (productCode?: string | null) => {
    if (productCode) {
      const res: SuccessResponse | null | undefined =
        await ProductService.delete(productCode);
      if (res) {
        research();
        toast.success(res?.message || "Success");
      }
    }
  };

  return (
    <ConfirmButton
      buttonText={t("button.delete")}
      buttonProps={{
        color: "danger",
        variant: "filled",
      }}
      confirmMessage={t("message.confirmDeleteProduct")}
      handleOk={() => handleDeleteUser(record?.productCode)}
    />
  );
};

const ProductManagement = () => {
  const { t } = useTranslation();
  const navigation = useNavigate();

  const isFirstRun = useRef<boolean>(true);
  const [dataTable, setDataTable] = useState<ProductDetail[]>([]);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [searchRequest, setSearchRequest] = useState<SearchProductListRequest>({
    keyword: "",
    size: 10,
    page: 0,
    sortBy: ["updated_at"],
    sortDesc: [true],
  });

  const research = async () => {
    const res: SearchProductResponse | null | undefined =
      await ProductService.search(searchRequest);
    setTotalRecord(res?.total || 0);
    setDataTable(res?.items || []);
  };
  const handleTableChange: TableProps<ProductDetail>["onChange"] = (
    _pagination,
    _filters,
    sorter,
  ) => {
    const s: any = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!s) {
      setSearchRequest((prevState) => ({
        ...prevState,
        sortBy: ["updated_date"],
        sortDesc: [true],
        page: 0,
      }));
      return;
    }

    const rawField =
      s.field ?? s.columnKey ?? (s.column && s.column.key) ?? s.key;
    if (!rawField) {
      setSearchRequest((prevState) => ({
        ...prevState,
        sortBy: ["updated_date"],
        sortDesc: [true],
        page: 0,
      }));
      return;
    }

    const field = String(rawField);
    let sortField = "updated_date";
    if (field === "productName" || field === "product_name" || field === "name")
      sortField = "productName";
    else if (field === "version") sortField = "version";

    const desc = s.order === "descend";
    setSearchRequest((prevState) => ({
      ...prevState,
      sortBy: [sortField],
      sortDesc: [desc],
      page: 0,
    }));
  };

  const columns: TableProps<ProductDetail>["columns"] = generateHeaderTable(
    t,
    research,
    searchRequest,
    navigation,
  );

  const onChangeKeySearch = (value: string) => {
    setSearchRequest((prevState) => ({ ...prevState, keyword: value }));
  };

  const onChangePageNum = (page: number) => {
    setSearchRequest((prevState) => ({
      ...prevState,
      page: page - 1,
    }));
  };

  const onChangePageSize = (pageSize: number) => {
    setSearchRequest((prevState) => ({
      ...prevState,
      page: 0,
      size: pageSize,
    }));
  };

  const onClickAddNewProduct = () => {
    navigation(MANAGER_PATH.PRODUCT.ADD);
  };

  useEffect(() => {
    async function searchUserList() {
      const res: SearchProductResponse | null | undefined =
        await ProductService.search(searchRequest);
      if (!res?.items || res?.items?.length === 0) {
        setTotalRecord(res?.total || 0);
        setDataTable([]);
        if (searchRequest.page > 0) {
          onChangePageNum(1);
          return;
        }
        // If backend explicitly returns total: 0, don't show "no results" warning
        if (
          !isFirstRun.current &&
          (res?.total === undefined || (res?.total !== null && res?.total > 0))
        ) {
          toast.warning(t("message.noResultsFound"));
        }
        return;
      }
      setTotalRecord(res?.total || 0);
      setDataTable(res?.items);
    }

    searchUserList().finally(() => {
      if (isFirstRun.current) {
        isFirstRun.current = false;
      }
    });
  }, [searchRequest, t]);

  return (
    <>
      <ManageContentLayout
        headerFunction={
          <>
            <Button
              icon={<PlusOutlined />}
              type={"primary"}
              size={"large"}
              onClick={() => onClickAddNewProduct()}
            >
              {t("button.addProduct")}
            </Button>
          </>
        }
      >
        <div
          className={"p-5 rounded-lg border bg-white"}
          style={{ minHeight: "753px" }}
        >
          <Flex justify={"flex-start"}>
            <Search
              style={{ width: 555 }}
              placeholder={t("placeholder.search")}
              onSearch={onChangeKeySearch}
              enterButton
              size={"large"}
            />
          </Flex>
          {/* Table function block */}
          <div className={"mt-5"}>
            <Table
              rowKey={"productCode"}
              columns={columns}
              dataSource={dataTable}
              pagination={false}
              onChange={handleTableChange}
            />
            <AppPagination
              className={"mt-3"}
              pageSize={searchRequest.size}
              onChangePageSize={onChangePageSize}
              onChangePage={onChangePageNum}
              currentPage={searchRequest.page}
              total={totalRecord}
            />
          </div>
        </div>
      </ManageContentLayout>
    </>
  );
};

export default ProductManagement;
